import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import ApiService from '../../../services/ApiService';

const FIELDS = [
  { key: 'name',        label: 'Full Name',   type: 'text',   required: true },
  { key: 'email',       label: 'Email',       type: 'email' },
  { key: 'phone',       label: 'Phone',       type: 'text' },
  { key: 'gender',      label: 'Gender',      type: 'select', options: [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }] },
  { key: 'dob',         label: 'Date of Birth', type: 'date' },
  { key: 'nationality', label: 'Nationality', type: 'text' },
  { key: 'emirate',     label: 'Emirate',     type: 'text' },
  { key: 'profession',  label: 'Profession',  type: 'text' },
  { key: 'height',      label: 'Height (cm)', type: 'number' },
  { key: 'weight',      label: 'Weight (kg)', type: 'number' },
  { key: 'interests',   label: 'Interests',   type: 'textarea', col: 'col-12' },
  { key: 'about',       label: 'About',       type: 'textarea', col: 'col-12' },
];

const toForm = (user) =>
  Object.fromEntries(
    FIELDS.map(({ key }) => {
      if (key === 'dob') return [key, user?.dob ? moment(user.dob).format('YYYY-MM-DD') : ''];
      return [key, user?.[key] ?? ''];
    })
  );

const EditUserModal = ({ show, user, onHide, onSaved }) => {
  const [form, setForm] = useState(toForm(user));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) setForm(toForm(user));
  }, [show, user]);

  const handleChange = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }

    // Blank inputs clear the field rather than saving an empty string.
    const payload = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === 'string' && v.trim() === '' ? null : v])
    );

    try {
      setIsSaving(true);
      const res = await ApiService.request({
        method: 'POST',
        url: `admin/users/createOrUpdate/${user._id}`,
        data: payload,
      });
      if (res.data.status) {
        toast.success(res.data.message);
        onSaved?.();
        onHide();
      } else {
        toast.error(res.data.message);
      }
    } catch {
      // ApiService already surfaced the error.
    } finally {
      setIsSaving(false);
    }
  };

  const renderInput = ({ key, type, options }) => {
    const common = {
      id: `edit-user-${key}`,
      className: type === 'select' ? 'form-select form-select-solid' : 'form-control form-control-solid',
      value: form[key] ?? '',
      onChange: handleChange(key),
    };
    if (type === 'select') {
      return (
        <select {...common}>
          <option value="">Select</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (type === 'textarea') return <textarea {...common} rows={3} />;
    return <input {...common} type={type} min={type === 'number' ? 0 : undefined} />;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            {FIELDS.map((field) => (
              <div key={field.key} className={`${field.col || 'col-md-6'} mb-5`}>
                <label htmlFor={`edit-user-${field.key}`} className={`form-label fw-semibold ${field.required ? 'required' : ''}`}>
                  {field.label}
                </label>
                {renderInput(field)}
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={onHide} disabled={isSaving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isSaving}>
            {isSaving ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> : null}
            Save Changes
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditUserModal;
